/** Sidebar project switcher: current project, list of projects, link to all projects, create project */

import { useCallback, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, ChevronsUpDown, FolderKanban, LayoutGrid, Plus } from 'lucide-react'
import { useProjects } from '../../hooks/use-projects'
import { useCurrentProjectSlug } from '../../hooks/use-current-project'
import { useAppStore } from '../../stores/app-store'
import { useT } from '../../i18n'
import { useDismiss } from './use-dismiss'
import { projectPath } from './nav-config'

const menuItemClass = 'flex min-h-9 w-full cursor-pointer items-center gap-2 rounded-md px-2.5 text-left text-sm text-fg-muted hover:bg-surface-hover hover:text-fg'

export function ProjectSwitcher() {
  const t = useT()
  const slug = useCurrentProjectSlug()
  const { data: projects, isLoading, isError, refetch } = useProjects()
  const openCreateProject = useAppStore((s) => s.setCreateProjectOpen)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setOpen(false), [])
  useDismiss(ref, open, close)

  const current = projects?.find((p) => p.slug === slug) ?? projects?.find((p) => p.isDefault)

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('topbar.switchProject')}
        className="flex min-h-10 w-full cursor-pointer items-center gap-2 rounded-md border border-border bg-surface px-2.5 text-left hover:border-border-strong"
      >
        <FolderKanban className="h-4 w-4 shrink-0 text-accent-text" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block text-xs text-fg-subtle">{t('nav.project')}</span>
          <span className="block truncate text-sm font-semibold text-fg">
            {isLoading ? t('common.loading') : (current?.name ?? slug)}
          </span>
        </span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-fg-subtle" aria-hidden="true" />
      </button>

      {open && (
        <div role="menu" className="absolute inset-x-0 top-full z-40 mt-1 rounded-lg border border-border bg-surface-raised p-1 shadow-xl">
          {isError && (
            <div className="px-2.5 py-2 text-sm text-danger">
              <p>{t('shell.projectsError')}</p>
              <button type="button" onClick={() => void refetch()} className="mt-1 cursor-pointer font-medium underline underline-offset-2">
                {t('common.retry')}
              </button>
            </div>
          )}
          <div className="max-h-72 overflow-y-auto">
            {projects?.map((project) => (
              <Link key={project.id} role="menuitem" to={projectPath(project.slug)} onClick={close} className={menuItemClass}>
                <span className="min-w-0 flex-1 truncate">{project.name}</span>
                {project.isDefault && <span className="shrink-0 text-xs text-fg-subtle">{t('shell.defaultProject')}</span>}
                {project.slug === current?.slug && <Check className="h-4 w-4 shrink-0 text-accent-text" aria-label={t('shell.currentProject')} />}
              </Link>
            ))}
          </div>
          <div className="mt-1 border-t border-border pt-1">
            <Link role="menuitem" to="/projects" onClick={close} className={menuItemClass}>
              <LayoutGrid className="h-4 w-4" aria-hidden="true" />
              {t('nav.allProjects')}
            </Link>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close()
                openCreateProject(true)
              }}
              className={menuItemClass}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('shell.newProject')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
